import Link from "next/link";
import type { ReactNode } from "react";
import { HubArrow } from "@/app/_components/conditions/hub/hub-icons";
import type { AreaViewUnit } from "../area-view-types";

/**
 * Shared presentational pieces for the lower half of the area page (G3 mockups): the white card
 * shell with its navy icon tile header, the pale teal action bar, a few line icons the hub icon
 * set does not draw, and small text helpers. No hooks, so server and client components can use
 * them.
 */

/** The white card used by the map, record, awards and (desktop) plan and other sports. */
export const G3_CARD =
  "overflow-hidden rounded-[20px] border border-line bg-white md:rounded-[22px] md:shadow-[0_14px_34px_rgba(22,35,59,0.06)]";

/** Sections jump to here under the sticky site header and phone jump bar. */
export const G3_SECTION = "scroll-mt-[124px] focus:outline-none";

/** The 24px line icons (currentColor stroke) used in card headers and link rows. */
export type G3IconName =
  | "pin" | "bars" | "trophy" | "calendar" | "book" | "briefcase" | "phone" | "map" | "compass" | "swap" | "grid" | "bookmark";

const ICON_PATHS: Readonly<Record<G3IconName, readonly string[]>> = {
  pin: ["M12 21s-7-6.2-7-11.5a7 7 0 0 1 14 0C19 14.8 12 21 12 21z", "M9.5 9.5a2.5 2.5 0 1 0 5.0 0a2.5 2.5 0 1 0 -5.0 0"],
  bars: ["M4 20H20", "M7 20V12", "M12 20V6", "M17 20V15"],
  trophy: [
    "M7.5 3.5H16.5V9.5A4.5 4.5 0 0 1 7.5 9.5Z",
    "M7.5 5.5H4.5V7A3.5 3.5 0 0 0 8 10.5M16.5 5.5H19.5V7A3.5 3.5 0 0 1 16 10.5",
    "M12 14V17.5",
    "M8.5 20.5H15.5M9.5 17.5H14.5V20.5H9.5Z",
  ],
  calendar: ["M4.5 6.5H19.5V19.5H4.5Z", "M4.5 10.5H19.5", "M8.5 4V8M15.5 4V8"],
  book: ["M4 5.5C6.5 4.5 9.5 4.5 12 6C14.5 4.5 17.5 4.5 20 5.5V19C17.5 18 14.5 18 12 19.5C9.5 18 6.5 18 4 19Z", "M12 6V19.5"],
  briefcase: ["M3.5 8H20.5V19H3.5Z", "M9 8V5.5H15V8", "M3.5 13H20.5"],
  phone: ["M7.5 3H16.5V21H7.5Z", "M10.5 18H13.5"],
  map: ["M3.5 6.5L9 4.5L15 6.5L20.5 4.5V17.5L15 19.5L9 17.5L3.5 19.5Z", "M9 4.5V17.5", "M15 6.5V19.5"],
  compass: ["M3.5 12a8.5 8.5 0 1 0 17 0a8.5 8.5 0 1 0 -17 0", "M15.5 8.5L13.5 13.5L8.5 15.5L10.5 10.5Z"],
  swap: ["M3 5.5H12.5M10 3L12.5 5.5L10 8M13 10.5H3.5M6 8L3.5 10.5L6 13"],
  grid: ["M4 4H10V10H4Z", "M14 4H20V10H14Z", "M4 14H10V20H4Z", "M14 14H20V20H14Z"],
  bookmark: ["M6.5 3.5H17.5V20.5L12 16.5L6.5 20.5Z", "M9.5 9L11.3 10.8L14.8 7.3"],
};

export function G3Icon({ name, size = 20, strokeWidth = 1.8, className = "" }: Readonly<{
  name: G3IconName;
  size?: number;
  strokeWidth?: number;
  className?: string;
}>) {
  const viewBox = name === "swap" ? "0 0 16 16" : "0 0 24 24";
  return (
    <svg
      width={size}
      height={size}
      viewBox={viewBox}
      fill="none"
      stroke="currentColor"
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
      className={`shrink-0 ${className}`}
    >
      {ICON_PATHS[name].map((d) => <path key={d} d={d} />)}
    </svg>
  );
}

/** The navy icon tile beside a card heading. */
export function G3IconTile({ name, size = "md" }: Readonly<{ name: G3IconName; size?: "md" | "lg" }>) {
  return (
    <span
      aria-hidden="true"
      className={`flex shrink-0 items-center justify-center bg-navy text-teal-light ${
        size === "lg" ? "size-11 rounded-[12px]" : "size-10 rounded-[11px] md:size-11 md:rounded-[12px]"
      }`}
    >
      <G3Icon name={name} />
    </span>
  );
}

/** A card heading: navy icon tile, h2 and a one-line lead. */
export function G3CardHeader({
  id,
  icon,
  title,
  lead,
  className = "",
}: Readonly<{ id: string; icon: G3IconName; title: ReactNode; lead?: ReactNode; className?: string }>) {
  return (
    <div className={`flex items-start gap-3 px-4 pb-2.5 pt-4 md:gap-3.5 md:px-[22px] md:pb-3 md:pt-5 ${className}`}>
      <G3IconTile name={icon} />
      <div className="min-w-0">
        <h2 id={id} className="text-[20px] font-bold leading-[1.2] text-navy">
          {title}
        </h2>
        {lead ? <p className="mt-[3px] text-[13px] leading-[1.45] text-[#566072] md:mt-1 md:text-[14px] md:leading-[1.5]">{lead}</p> : null}
      </div>
    </div>
  );
}

/** The pale teal full-width action at the foot of a card ("See all season awards"). */
export function G3ActionLink({ href, children, className = "" }: Readonly<{ href: string; children: ReactNode; className?: string }>) {
  return (
    <Link
      href={href}
      className={`group flex min-h-12 items-center justify-between gap-2.5 rounded-[12px] border border-[#BFDDE1] bg-[#F2F8F8] px-3.5 text-[14.5px] font-bold text-teal-dark no-underline transition-colors hover:border-teal hover:text-teal md:px-4 md:text-[15px] ${className}`}
    >
      {children}
      <HubArrow />
    </Link>
  );
}

/** A small uppercase group label inside a card ("Monthly award"). */
export function G3GroupLabel({ children, className = "" }: Readonly<{ children: ReactNode; className?: string }>) {
  return (
    <p className={`text-[11.5px] font-bold uppercase tracking-[0.1em] text-[#566072] ${className}`}>{children}</p>
  );
}

// ── Text helpers ─────────────────────────────────────────────────────────

const UNIT_SINGULAR: Readonly<Record<AreaViewUnit, string>> = { beaches: "beach", spots: "spot", resorts: "resort", venues: "venue" };

/** "1 beach", "17 beaches". */
export function countUnit(count: number, unit: AreaViewUnit): string {
  return `${count} ${count === 1 ? UNIT_SINGULAR[unit] : unit}`;
}

export function singularUnit(unit: AreaViewUnit): string {
  return UNIT_SINGULAR[unit];
}

/** "a", "a and b", "a, b and c". */
export function joinList(items: readonly string[], conjunction = "and"): string {
  if (items.length <= 1) return items[0] ?? "";
  return `${items.slice(0, -1).join(", ")} ${conjunction} ${items[items.length - 1]}`;
}

/** Lower-case a label for running text, keeping acronyms ("MTB", "DJ"). */
export function lowerLabel(label: string): string {
  return label
    .split(" ")
    .map((word) => (word.length > 1 && word === word.toUpperCase() ? word : word.toLowerCase()))
    .join(" ");
}

const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
const MONTHS_SHORT = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/** 1-based month to "March". */
export function monthLong(month: number): string {
  return MONTHS[(month - 1 + 12) % 12] ?? "";
}

/** 1-based month to "Mar" (fixed names: the system formatter writes "Sept"). */
export function monthShort(month: number): string {
  return MONTHS_SHORT[(month - 1 + 12) % 12] ?? "";
}

/** "2026-03-16" to "16 March". */
export function dayMonthLong(dateISO: string): string {
  const day = Number(dateISO.slice(8, 10));
  const month = Number(dateISO.slice(5, 7));
  return Number.isFinite(day) && month >= 1 ? `${day} ${monthLong(month)}` : dateISO;
}

/** The location photo path for a slug with a committed photo. */
export function locationPhoto(slug: string): string {
  return `/images/locations/${slug}.jpg`;
}
